import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Sparkles, Users } from 'lucide-react';
import clsx from 'clsx';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { tablesService, type DiningTable, type DayBookingRow, type SuggestedTable } from '@/services/tables.service';

interface Props {
  open: boolean;
  onClose: () => void;
  booking: DayBookingRow | null;
  tables: DiningTable[];
  onAssigned: () => void;
}

export function AssignTableModal({ open, onClose, booking, tables, onAssigned }: Props) {
  const [selected, setSelected] = useState<string>('');
  const [suggestion, setSuggestion] = useState<SuggestedTable | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !booking) return;
    setSelected(booking.tableUid ?? '');
    setSuggestion(null);
    tablesService.suggest({ bookingUid: booking.bookingUid }).then((r) => setSuggestion(r.table)).catch(() => {});
  }, [open, booking]);

  if (!booking) return null;
  const partySize = booking.partySize ?? 1;
  const chosen = tables.find((t) => t.tableUid === selected);
  const tooSmall = !!chosen && chosen.seats < partySize;

  const confirm = async (force: boolean) => {
    if (!selected) { toast.error('Pick a table first.'); return; }
    setBusy(true);
    try {
      await tablesService.assign(booking.bookingUid, selected, force);
      toast.success('Table assigned');
      onAssigned();
      onClose();
    } catch (e: any) {
      if (!e?.response) toast.error('Could not assign the table.');
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Assign a table — ${booking.customerName ?? 'Guest'}`} widthClass="max-w-md">
      <div className="mb-4 flex items-center gap-2 text-xs text-text-muted">
        <Users size={13} /> Party of {partySize}
        {suggestion && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-medium text-primary">
            <Sparkles size={11} /> Suggested: {suggestion.name}
          </span>
        )}
      </div>

      <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
        {tables.filter((t) => t.isActive).map((t) => {
          const isSuggested = suggestion?.tableUid === t.tableUid;
          const small = t.seats < partySize;
          return (
            <button
              key={t.tableUid}
              type="button"
              onClick={() => setSelected(t.tableUid)}
              className={clsx(
                'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                selected === t.tableUid ? 'border-primary bg-primary-soft' : 'border-border hover:border-border-strong',
              )}
            >
              <span className="flex items-center gap-2 font-medium text-text-primary">
                {t.name}
                {isSuggested && <Sparkles size={12} className="text-primary" />}
              </span>
              <span className={clsx('text-xs', small ? 'text-warning' : 'text-text-muted')}>
                {t.seats} seats{t.area ? ` · ${t.area}` : ''}
              </span>
            </button>
          );
        })}
        {tables.filter((t) => t.isActive).length === 0 && (
          <p className="py-6 text-center text-sm text-text-muted">No active tables yet — add one from Manage tables.</p>
        )}
      </div>

      {tooSmall && (
        <p className="mt-3 text-xs text-warning">
          {chosen!.name} seats {chosen!.seats}, fewer than the party of {partySize}. You can still seat them here.
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={busy} disabled={!selected} onClick={() => confirm(tooSmall)}>
          {tooSmall ? 'Assign anyway' : 'Assign table'}
        </Button>
      </div>
    </Modal>
  );
}
